import React, { Fragment, useEffect, useState } from 'react';
import classNames from 'classnames';

import { useAttachment } from '../../hooks';
import { ReactComponent as AttachmentIcon } from '../../images/attachment-icon.svg';
import { ReactComponent as DownloadAttachmentIcon } from '../../images/download-attachment.svg';
import { Attachment } from '../../types';
import { DotsIndicator } from '../DotsIndicator';

import styles from './MessageAttachment.module.css';

export function MessageAttachment({
  attachment,
  messageId,
  timelineRef,
  messageIdSnapshot,
}: {
  attachment: Attachment;
  messageId: string;
  timelineRef: React.RefObject<HTMLElement>;
  messageIdSnapshot: string[];
}) {
  const [hasLoaded, setHasLoaded] = useState(false);
  const [mediaType] = attachment.contentType.split('/');
  const viewableMedia = ['audio', 'image', 'video'].includes(mediaType);

  const {
    attachmentBlobUrl,
    attachmentTokenUrl,
    initiateFetching,
    invalidateAttachmentToken,
  } = useAttachment(messageId, attachment.id, viewableMedia);

  let inlineMediaFragment;

  useEffect(() => {
    if (
      !(attachmentBlobUrl && attachmentTokenUrl) ||
      !attachmentTokenUrl.includes(messageId) ||
      !attachmentTokenUrl.includes(`/${attachment.id}?`)
    ) {
      initiateFetching(messageId, attachment.id);
    }
  }, [attachment.id, attachmentBlobUrl, attachmentTokenUrl, initiateFetching, messageId]);

  if (viewableMedia) {
    switch (mediaType) {
      case 'audio':
        inlineMediaFragment = (
          <audio
            className={styles.attachmentAudio}
            controls
            preload="meta"
            controlsList="nodownload"
          >
            <source src={attachmentBlobUrl} type={attachment.contentType} />
          </audio>
        );
        break;
      case 'image':
        inlineMediaFragment = (
          <img
            alt={attachment.name}
            className={styles.attachmentImage}
            onLoad={() => {
              setHasLoaded(true);
              if (messageIdSnapshot.includes(messageId)) {
                //eslint-disable-next-line scanjs-rules/call_setTimeout
                setTimeout(() => {
                  timelineRef.current && timelineRef.current.scrollIntoView();
                }, 100);
              }
            }}
            src={attachmentBlobUrl}
          />
        );
        break;
      case 'video':
        inlineMediaFragment = (
          <video
            className={styles.attachmentVideo}
            controls
            onLoadStart={() => {
              setHasLoaded(true);
              if (messageIdSnapshot.includes(messageId)) {
                timelineRef.current && timelineRef.current.scrollIntoView();
              }
            }}
            preload="none"
            disablePictureInPicture
            controlsList="nodownload"
          >
            <source src={attachmentBlobUrl} type={attachment.contentType} />
          </video>
        );
        break;
    }
  }

  const anchorProps = {
    onClick: () => {
      invalidateAttachmentToken();
      window.open(attachmentTokenUrl, '_blank');
      return true;
    },
    'aria-label': `open ${attachment.name}`,
    download: attachment.name,
  };

  const isTokenLoaded = !!attachmentTokenUrl;
  const isInlineLoaded = viewableMedia && attachmentBlobUrl;
  const shouldShowLoading = !isTokenLoaded || (viewableMedia && !isInlineLoaded && !hasLoaded);

  return (
    <div
      className={classNames({
        [styles.attachment]: true,
        [styles[mediaType]]: true,
        [styles.loading]: shouldShowLoading,
      })}
    >
      {shouldShowLoading ? (
        <div className={styles.dots}>
          <DotsIndicator color="#65659e" fullHeight={false} />
        </div>
      ) : (
        <Fragment>
          {isInlineLoaded && inlineMediaFragment}
          {mediaType !== 'video' && (
            <a className={styles.attachmentApplication} {...anchorProps} rel="noopener noreferrer">
              <DefaultAttachment
                attachmentName={attachment.name} /*measure={() => messageLoaded(messageId)}*/
              />
            </a>
          )}
        </Fragment>
      )}
    </div>
  );
}

function DefaultAttachment({ attachmentName }: { attachmentName: string }) {
  return (
    <div className={styles.attachmentContainer}>
      <div className={styles.icon}>
        <AttachmentIcon />
      </div>
      <div className={styles.name}>{attachmentName}</div>
      <div className={styles.icon}>
        <DownloadAttachmentIcon />
      </div>
    </div>
  );
}

export default MessageAttachment;
